import React from 'react';
import { Calendar, Flame, Minus, Plus } from 'lucide-react';
import { heatingTypes, heatingSystemTypes } from '../../lib/constants';
import type { StepProps } from './types';

export default function BuildingCharacteristicsStep({ 
  formData, 
  handleInputChange, 
  handleNumberChange,
  errors 
}: StepProps) {
  return (
    <div className="space-y-8">
      <h3 className="text-lg font-medium text-gray-900">
        Caractéristiques de l'immeuble
      </h3>
      
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nombre d'étages de l'immeuble*
            </label>
            <div className="flex items-center">
              <button
                type="button"
                onClick={() => handleNumberChange('nbre_etages', false)}
                className="p-2 border border-gray-300 rounded-l-lg hover:bg-gray-50"
              >
                <Minus className="h-4 w-4" />
              </button>
              <input
                type="number"
                name="nbre_etages"
                value={formData.nbre_etages || 1}
                onChange={handleInputChange}
                className={`w-16 text-center border-y border-gray-300 ${
                  errors.nbre_etages ? 'border-red-500' : ''
                }`}
                min="1"
              />
              <button
                type="button"
                onClick={() => handleNumberChange('nbre_etages', true)}
                className="p-2 border border-gray-300 rounded-r-lg hover:bg-gray-50"
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
            {errors.nbre_etages && (
              <p className="mt-1 text-sm text-red-600">{errors.nbre_etages}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Étage du bien*
            </label>
            <div className="flex items-center">
              <button
                type="button"
                onClick={() => handleNumberChange('etage_bien', false)}
                className="p-2 border border-gray-300 rounded-l-lg hover:bg-gray-50"
              >
                <Minus className="h-4 w-4" />
              </button>
              <input
                type="number"
                name="etage_bien"
                value={formData.etage_bien}
                onChange={handleInputChange}
                className={`w-16 text-center border-y border-gray-300 ${
                  errors.etage_bien ? 'border-red-500' : ''
                }`}
                min="0"
                max="30"
              />
              <button
                type="button"
                onClick={() => handleNumberChange('etage_bien', true)}
                className="p-2 border border-gray-300 rounded-r-lg hover:bg-gray-50"
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
            {errors.etage_bien && (
              <p className="mt-1 text-sm text-red-600">{errors.etage_bien}</p>
            )}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Année de construction*
          </label>
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <input
              type="number"
              name="annee_construction"
              value={formData.annee_construction === '' ? '' : formData.annee_construction}
              onChange={handleInputChange}
              className={`pl-10 w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 ${
                errors.annee_construction ? 'border-red-500' : ''
              }`}
              min="1800"
              max={new Date().getFullYear()}
              placeholder="Année de construction"
            />
          </div>
          {errors.annee_construction && (
            <p className="mt-1 text-sm text-red-600">{errors.annee_construction}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Nature du chauffage*
          </label>
          <select
            name="nature_chauffage"
            value={formData.nature_chauffage}
            onChange={handleInputChange}
            className={`w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 ${
              errors.nature_chauffage ? 'border-red-500' : ''
            }`}
            required
          >
            {heatingTypes.map(({ value, label }) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
          {errors.nature_chauffage && (
            <p className="mt-1 text-sm text-red-600">{errors.nature_chauffage}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Type de chauffage*
          </label>
          <select
            name="type_chauffage"
            value={formData.type_chauffage}
            onChange={handleInputChange}
            className={`w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 ${
              errors.type_chauffage ? 'border-red-500' : ''
            }`}
            required
          >
            {heatingSystemTypes.map(({ value, label }) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
          {errors.type_chauffage && (
            <p className="mt-1 text-sm text-red-600">{errors.type_chauffage}</p>
          )}
        </div>

        <div className="space-y-4">
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              name="ascenseur"
              checked={formData.ascenseur}
              onChange={handleInputChange}
              className="w-5 h-5 rounded border-gray-300 
                checked:bg-gradient-to-r checked:from-[#2A4365] checked:via-[#4A5568] checked:to-[#C4A052]
                focus:ring-[#2A4365] focus:ring-offset-2 transition-all duration-300"
            />
            <span className="text-sm text-gray-700">Ascenseur</span>
          </label>

          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              name="place_parking"
              checked={formData.place_parking}
              onChange={handleInputChange}
              className="w-5 h-5 rounded border-gray-300 
                checked:bg-gradient-to-r checked:from-[#2A4365] checked:via-[#4A5568] checked:to-[#C4A052]
                focus:ring-[#2A4365] focus:ring-offset-2 transition-all duration-300"
            />
            <span className="text-sm text-gray-700">Place de parking</span>
          </label>
        </div>
      </div>
    </div>
  );
}